// Activo Fijo → Valorización → Impuesto diferido (NIC 12).
//
// El módulo ya calculaba la diferencia entre depreciación contable y fiscal y
// la dejaba morir en un reporte. Aquí desemboca: se compara el saldo de
// impuesto diferido que DEBERÍA existir al cierre con el que ya se registró, y
// se contabiliza sólo el movimiento del ejercicio.
//
// ALCANCE: cubre las diferencias que ORIGINA el activo fijo. No es el impuesto
// diferido de toda la entidad, y la pantalla lo dice para que nadie lo confunda.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Calculator, Landmark, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, ImpDiferido, afRpc, fieldCls, money } from './shared';

interface Resp { preview?: boolean; ok?: boolean; contabilizada?: boolean; aviso?: string; resumen: ImpDiferido }

export function ImpuestoDiferidoAF() {
  const qc = useQueryClient();
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [res, setRes] = useState<ImpDiferido | null>(null);

  const calcular = useMutation({
    mutationFn: () => afRpc<Resp>('af_impuesto_diferido', { _anio: anio, _confirmar: false }),
    onSuccess: (r) => setRes(r.resumen),
    onError: (e) => { setRes(null); toast.error(getErrorMessage(e)); },
  });
  const registrar = useMutation({
    mutationFn: () => afRpc<Resp>('af_impuesto_diferido', { _anio: anio, _confirmar: true }),
    onSuccess: (r) => {
      setRes(r.resumen);
      if (r.contabilizada) toast.success('Impuesto diferido registrado · asiento generado');
      else toast.info(r.aviso ?? 'Sin movimiento que registrar');
      void qc.invalidateQueries({ queryKey: ['af-imp-dif'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const { data: historico = [] } = useQuery<ImpDiferido[]>({
    queryKey: ['af-imp-dif'],
    queryFn: () => afRpc<ImpDiferido[]>('af_impuesto_diferido_historico').catch(() => [] as ImpDiferido[]),
    retry: false,
  });

  // Contable > fiscal ⇒ se dedujo menos de lo que se gastó ⇒ ACTIVO diferido.
  const esActivo = (res?.saldo_requerido ?? 0) >= 0;

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <h1 className="flex items-center gap-2 text-xl font-semibold"><Landmark className="h-5 w-5" /> Impuesto diferido</h1>
      <p className="text-sm text-muted-foreground">
        La diferencia entre la depreciación <b>contable</b> y la <b>fiscal</b> es temporaria: lo que hoy no se deduce, se deduce después.
        La NIC 12 pide reconocer ese efecto. Aquí se calcula el saldo que debe existir al cierre y se contabiliza sólo el movimiento del año.
      </p>
      <p className="text-xs text-muted-foreground">
        Cubre las diferencias que <b>origina el activo fijo</b>. El impuesto diferido de toda la entidad puede tener otras fuentes (provisiones, pérdidas tributarias) que no se ven desde aquí.
        El impuesto diferido del <b>superávit por revaluación</b> no entra en este cálculo: se registra solo, contra el patrimonio, en el momento de la revaluación.
      </p>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <Campo label="Ejercicio">
            <input type="number" className={`${fieldCls} w-28`} value={anio}
              onChange={(e) => { setAnio(Number(e.target.value)); setRes(null); }} />
          </Campo>
          <Button variant="outline" isLoading={calcular.isPending} onClick={() => calcular.mutate()}>
            <Calculator className="mr-1 h-4 w-4" /> Calcular
          </Button>
          <Button isLoading={registrar.isPending} disabled={!res || Number(res.movimiento) === 0}
            onClick={() => registrar.mutate()}>
            Registrar y contabilizar
          </Button>
          {calcular.isPending && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
        </CardContent>
      </Card>

      {res && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Ejercicio {res.anio} · tarifa {Number(res.tarifa)}%</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 pr-3">Diferencia acumulada al cierre <span className="text-xs text-muted-foreground">(depreciación contable − fiscal)</span></td>
                    <td className="py-2 text-right font-mono tabular-nums">{money(res.base_diferencia)}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-3">Saldo que debe existir <span className="text-xs text-muted-foreground">(diferencia × tarifa)</span></td>
                    <td className="py-2 text-right font-mono tabular-nums font-medium">{money(res.saldo_requerido)}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-3">Ya registrado en ejercicios anteriores</td>
                    <td className="py-2 text-right font-mono tabular-nums">{money(res.saldo_anterior)}</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-3 font-semibold">Movimiento de este ejercicio</td>
                    <td className={`py-2 text-right font-mono tabular-nums font-semibold ${Number(res.movimiento) < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>{money(res.movimiento)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="rounded-md border bg-muted/30 p-3 text-sm">
              {Number(res.base_diferencia) === 0 ? (
                <p>No hay diferencia entre la depreciación contable y la fiscal: no nace impuesto diferido por este concepto.</p>
              ) : esActivo ? (
                <p><b>Activo por impuesto diferido.</b> La contabilidad deprecia más de lo que el fisco admite deducir hoy: ese gasto se deducirá en ejercicios futuros, y eso es un derecho a pagar menos impuesto más adelante.</p>
              ) : (
                <p><b>Pasivo por impuesto diferido.</b> El fisco admite deducir más de lo que la contabilidad deprecia: hoy se paga menos impuesto y ese diferimiento se revierte en ejercicios futuros.</p>
              )}
            </div>

            {Number(res.movimiento) === 0 && (
              <div className="flex items-start gap-2 rounded-md border border-amber-400 bg-amber-50 p-3 text-sm dark:border-amber-900/50 dark:bg-amber-900/15">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <p className="text-amber-800 dark:text-amber-300">El saldo ya registrado coincide con el requerido: no hay nada que contabilizar en este ejercicio.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {historico.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Ejercicios registrados</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Ejercicio</th><th className="py-2 pr-3 text-right">Tarifa</th>
                <th className="py-2 pr-3 text-right">Diferencia</th><th className="py-2 pr-3 text-right">Saldo</th>
                <th className="py-2 text-right">Movimiento</th>
              </tr></thead>
              <tbody>
                {historico.map((h) => (
                  <tr key={h.anio} className="border-b last:border-0">
                    <td className="py-2 pr-3">{h.anio}</td>
                    <td className="py-2 pr-3 text-right">{Number(h.tarifa)}%</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(h.base_diferencia)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(h.saldo_requerido)}</td>
                    <td className="py-2 text-right font-mono tabular-nums">{money(h.movimiento)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
